import { createId } from "../../app/runtimeContext";
import type { ObservabilityClient } from "../../observability/types";
import type { AgentRuntime } from "../agents/agentRuntime";
import type { RequestRuntimeContext } from "../context/runtimeContext";
import type { MemoryEngine, MemoryEntry } from "../memory/memory";
import type { TaskEngine, RuntimeTask } from "../tasks/taskEngine";
import type { ConversationManager } from "./conversationManager";
import type { DecisionEngine } from "./decisionEngine";
import type { ExecutionCoordinator } from "./executionCoordinator";
import type { GoalManager } from "./goalManager";
import type { PlanningEngine } from "./planningEngine";
import type { ReasoningPipeline } from "./reasoningPipeline";
import type { ExecutionPlan, Goal, ReasoningTrace } from "./types";

export type CyanRequest = {
  prompt: string;
  source: "owner" | "system" | "agent";
};

export type CyanExecutionPlan = {
  id: string;
  summary: string;
  taskTitles: string[];
  memory: MemoryEntry[];
};

export type CyanReport = {
  plan: CyanExecutionPlan;
  tasks: RuntimeTask[];
  message: string;
};

export type CyanIntelligenceReport = {
  goal: Goal;
  plan: ExecutionPlan;
  reasoning: ReasoningTrace;
  decisionId: string;
  executionStatus: "coordinated" | "blocked";
};

type CyanCoreIntelligence = {
  goals: GoalManager;
  planning: PlanningEngine;
  decisions: DecisionEngine;
  conversations: ConversationManager;
  reasoning: ReasoningPipeline;
  execution: ExecutionCoordinator;
};

export class CyanCore {
  constructor(
    private readonly memory: MemoryEngine,
    private readonly tasks: TaskEngine,
    private readonly agents: AgentRuntime,
    private readonly observability: ObservabilityClient,
    private readonly intelligence?: CyanCoreIntelligence,
  ) {}

  async receiveRequest(
    request: CyanRequest,
    context: RequestRuntimeContext,
  ): Promise<CyanReport> {
    const memory = this.memory.list();
    const plan: CyanExecutionPlan = {
      id: createId("cyan_plan"),
      summary: `Plan created for ${request.source} request.`,
      taskTitles: [`Review request: ${request.prompt.slice(0, 80)}`],
      memory,
    };
    const task = await this.tasks.createTask({
      organizationId: "org_francacyan",
      productId: context.currentProductId ?? "product_ss-core",
      departmentId: context.currentDepartmentId,
      agentId: context.currentAgentId,
      title: plan.taskTitles[0],
      description: request.prompt,
      status: "Planning",
      dependencies: [],
      owner: context.userId ?? "owner",
      memoryLinks: memory.map((entry) => entry.id),
    });
    await this.observability.event({
      category: "cyan",
      eventName: "cyan_plan_created",
      message: plan.summary,
      correlationId: context.correlationId,
    });
    void this.agents;
    return {
      plan,
      tasks: [task],
      message:
        "Cyan created a plan and task foundation. Autonomous execution is not enabled.",
    };
  }

  async processIntelligenceRequest(
    request: CyanRequest,
    context: RequestRuntimeContext,
  ): Promise<CyanIntelligenceReport> {
    if (!this.intelligence) {
      throw new Error("Cyan intelligence modules are not registered.");
    }

    const goal = await this.intelligence.goals.createGoal({
      organizationId: context.organizationId,
      productId: context.currentProductId,
      departmentId: context.currentDepartmentId,
      owner: context.userId ?? "owner",
      scope: context.currentDepartmentId ? "department" : "company",
      title: `Resolve request from ${request.source}`,
      description: request.prompt,
      milestones: [
        { title: "Plan", weight: 1 },
        { title: "Coordinate", weight: 1 },
        { title: "Reflect", weight: 1 },
      ],
    });
    const reasoning = this.intelligence.reasoning.run({
      organizationId: context.organizationId,
      prompt: request.prompt,
      context: this.memory.list().map((entry) => entry.summary),
    });
    const plan = await this.intelligence.planning.createPlan({
      goal,
      objectiveTitles: [
        "Understand context",
        "Create execution path",
        "Prepare reflection",
      ],
    });
    const decision = await this.intelligence.decisions.decide({
      organizationId: context.organizationId,
      question: `How should Cyan handle: ${request.prompt}`,
      options: [
        {
          id: "plan-first",
          label: "Plan before execution",
          cost: 0.2,
          risk: 0.1,
          benefit: 0.9,
          confidence: 0.85,
          dependencies: 0.2,
          urgency: 0.7,
          strategicValue: 0.9,
          technicalDebt: 0.1,
        },
        {
          id: "execute-now",
          label: "Execute immediately",
          cost: 0.4,
          risk: 0.7,
          benefit: 0.5,
          confidence: 0.45,
          dependencies: 0.5,
          urgency: 0.8,
          strategicValue: 0.4,
          technicalDebt: 0.6,
        },
      ],
    });
    const conversation =
      await this.intelligence.conversations.createConversation({
        organizationId: context.organizationId,
        productId: context.currentProductId,
        title: goal.title,
        context: { source: request.source },
      });
    await this.intelligence.conversations.addMessage(conversation, {
      role: request.source === "agent" ? "agent" : "owner",
      content: request.prompt,
      intent: "plan",
      reasoning: reasoning.steps.map((step) => step.output ?? step.name),
      memoryReferences: [],
      knowledgeReferences: [],
      toolUsage: [],
      decisionReferences: [decision.id],
    });
    const execution = await this.intelligence.execution.coordinate(plan, {
      userId: context.userId ?? "owner",
      email: context.userId ?? "owner",
      organizationId: context.organizationId,
      roles: ["owner"],
      permissions: ["*"],
    });
    await this.observability.event({
      category: "cyan",
      eventName: "cyan_intelligence_request_processed",
      message: `Cyan processed request with ${plan.objectives.length} objectives.`,
      correlationId: context.correlationId,
    });
    return {
      goal,
      plan,
      reasoning,
      decisionId: decision.id,
      executionStatus: execution.status,
    };
  }
}
